import React, { useContext, useEffect, useState } from 'react';
import { i18nContext } from '@/components/pages/assessments/context/i18n.context';
import { stepContext } from '@/components/pages/assessments/context/step.context';
import { displayContext } from '@/components/pages/assessments/context/display.context';
import { UserContext } from '@/components/pages/assessments/context/user.context';
import useLocation from '@/hooks/useLocation';
import { modalContext } from '@/context/modal.context';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import DetailedProgressSummary from '@/components/pages/assessments/common/DetailedProgressSummary';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from '@/components/Button/Button';
import ResourceResult from '@/components/Assessments/ResourceResult/ResourceResult';
import { extractLocation, extractMappedValues } from '@/components/pages/assessments/util';
import SearchResourceForm from '@/components/Search/SearchResourceForm/SearchResourceForm';
import Skeleton from 'react-loading-skeleton';
import findIndex from 'lodash/findIndex';
import clone from 'lodash/clone';
import uniqBy from 'lodash/uniqBy';
import isEmpty from 'lodash/isEmpty';
import 'react-loading-skeleton/dist/skeleton.css';

const ResultsScreen = () => {
  const { t, xt, locale } = useContext(i18nContext);
  const { css, cols } = useContext(displayContext);
  const { answers, fetchedAnswers, api, assessment, components, handleRestart, metadata, screenData, total, invitation } = useContext(stepContext);
  const { user, agencyClient } = useContext(UserContext);
  const { locationContext } = useLocation();
  const { enqueueModal } = useContext(modalContext);
  const buttons = metadata?.ui?.buttons || {};
  const { locationHelpers } = locationContext;
  const [currentLocation, setCurrentLocation] = useState(
    () =>
      extractLocation({ metadata, screenData, questions: assessment.questions, answers }) ||
      locationHelpers.determineAssessmentDefaultLocation(locationContext.location, assessment?.enabled_regions)
  ); //should be an object, extract location or pull from location context
  const [results, setResults] = useState({});
  const [opportunities, setOpportunities] = useState([]);
  const [visibleResultsCount, setVisibleResultsCount] = useState(0);
  const [hideShowMoreButton, setHideShowMoreButton] = useState(false);
  const [loading, setLoading] = useState({ answers: true, results: true, resources: false });
  const [locationError, setLocationError] = useState(false);
  const origin_type = metadata?.options?.access_event_origin_type;
  const secondOrigin = origin_type ? { origin_type, invitation } : null;
  const assessmentId = assessment.id;

  const pickUnique = (arrayOfArrays, finalArray = []) => {
    for (let j = 0; j < arrayOfArrays.length; j++) {
      let nextItem = arrayOfArrays[j].shift();
      while (nextItem && findIndex(finalArray, { id: nextItem.id }) > -1) {
        nextItem = arrayOfArrays[j].shift();
      }
      if (nextItem) {
        finalArray.push(nextItem);
      }
    }
    return finalArray;
  };

  const fetchOpportunitiesByTags = (tags) => {
    return Promise.all(
      tags.map((tag) => {
        let opts = {
          queryBounds: {
            lat: currentLocation.lat,
            lng: currentLocation.lng
          },
          titles_only: false,
          attributes_to_include: 'locations,properties,access_instructions',
          locale
        };
        if (metadata?.options?.result_logic) {
          switch (metadata.options.result_logic.type) {
            case 'count_per_tag':
              opts.limit = metadata.options.result_logic.count * 2;
              break;
          }
        }
        return api.loadResourcesForTags([tag], opts);
      })
    );
  };

  const handleOppsFetched = (arraysOfOpps) => {
    if (metadata?.options?.result_logic) {
      switch (metadata.options.result_logic.type) {
        case 'count_per_tag':
          let finalArray = [];
          let oppsToProcess = clone(arraysOfOpps);
          for (let i = 0; i < metadata.options.result_logic.count; i++) {
            finalArray = pickUnique(oppsToProcess, finalArray);
          }
          const initialResultsCount = finalArray.length;

          while (oppsToProcess.map((arr) => arr.length).reduce((accumulator, current) => accumulator + current, 0) > 0) {
            finalArray = pickUnique(oppsToProcess, finalArray);
          }

          return { oppList: finalArray, visibleResultsCount: initialResultsCount };
      }
    } else {
      let oppList = uniqBy(arraysOfOpps.flat(), 'id');
      return { oppList, visibleResultsCount: Math.min(oppList.length, 10) };
    }
  };

  const processResults = ({ oppList, visibleResultsCount }) => {
    setLoading((prevState) => ({ ...prevState, resources: false }));
    setVisibleResultsCount(visibleResultsCount);
    setOpportunities(oppList);
    setHideShowMoreButton(visibleResultsCount >= oppList.length);
  };

  const handleResultsResponse = async (result) => {
    let data = result?.data;
    if (data) {
      setResults(data);
      setLoading((prevState) => ({ ...prevState, results: false }));
    } else {
      setResults({});
      setLoading((prevState) => ({ ...prevState, results: false }));
      if (result?.response?.status === 403) {
        // TODO confirm-login
        setTimeout(() => window.location.reload(), 10);
      }
    }
    return data;
  };

  const loadAssessmentResults = async () => {
    setLoading((prevState) => ({ ...prevState, resources: true }));
    if (!user?.id) {
      setLoading((prevState) => ({ ...prevState, results: true }));
      return;
    }

    const res1 = await api.getAssessmentResults(assessmentId, agencyClient?.id, invitation);
    const res2 = await handleResultsResponse(res1);
    let tagsToSearch = [];
    let arraysOfOpps;

    // if assessment with custom results, and the answer data is available in memory, start the resource search immediately
    if (metadata?.custom_mapping && Object.keys(answers).length && loading.results) {
      tagsToSearch = getTagListFromCustomMapping();
    } else {
      if (res2?.custom_results?.tags?.length) {
        tagsToSearch = res2.custom_results.tags;
      }
      // TODO: handle resource search based on result data
    }
    if (tagsToSearch?.length) {
      arraysOfOpps = await fetchOpportunitiesByTags(tagsToSearch);
    } else if (res2?.remote_engine_results?.length > 1) {
      // TODO: key 1 should be a result recommendation key - fetch the data
      arraysOfOpps = [];
    } else {
      return;
    }

    processResults(handleOppsFetched(arraysOfOpps));
  };

  const getTagListFromCustomMapping = () => {
    let tagsToSearch = [];
    const customQuestionIds = Object.keys(metadata.custom_mapping);
    customQuestionIds.forEach((qId) => {
      if (metadata?.custom_mapping[qId]?.answers && answers?.[qId]) {
        if (Array.isArray(answers[qId].value)) {
          answers[qId].value.forEach((answerValue) => {
            const tags = extractMappedValues(metadata.custom_mapping[qId].answers, answerValue);
            tagsToSearch = tagsToSearch.concat(tags);
          });
        } else {
          const tags = extractMappedValues(metadata.custom_mapping[qId].answers, answers[qId].value);
          tagsToSearch = tagsToSearch.concat(tags);
        }
      }
    });
    return tagsToSearch;
  };

  const openRestartConfirmationModal = () => {
    const done = ({ close, res }, canProceed) => {
      if (canProceed) {
        handleRestart(res); // this is causing the restart api call to happen again
      }
      close();
    };
    components().prompts.modal.confirm_restart_assessment({
      done,
      t,
      assessmentId: assessmentId,
      agencyClientId: agencyClient?.id
    });
  };

  useEffect(() => {
    const finishLoading = () => setLoading((prevState) => ({ ...prevState, answers: false }));

    const fetchLocationFromZipCode = (zipCode) => {
      return locationHelpers
        .getLocationFromZipCode(zipCode)
        .then((location) => {
          setCurrentLocation(location);
          finishLoading();
        })
        .catch((error) => {
          setLocationError(t('Error searching in your location, please update your location and try again'));
          console.error(error);
          finishLoading();
        });
    };
    // When a user is revisiting an assessment's results, 'answers' is an empty object but 'fetchedAnswers'
    // will be the data pulled from the server. In this scenario, 'currentLocation' would default
    // to the user's search location or the assessment's region (if one exists).
    // The following logic checks for any location-typed questions that were part of the assessment and
    // uses that value on the initial load.
    if (isEmpty(answers) && !isEmpty(fetchedAnswers)) {
      let locationValue = extractLocation({ questions: assessment.questions, answers: fetchedAnswers });
      if (locationValue) {
        if (typeof locationValue == 'string') {
          fetchLocationFromZipCode(locationValue);
        } else if (typeof locationValue == 'object') {
          setCurrentLocation(locationValue);
          finishLoading();
        } else {
          finishLoading();
        }
      } else {
        finishLoading();
      }
    } else if (!isEmpty(answers) && typeof currentLocation === 'string') {
      fetchLocationFromZipCode(currentLocation);
    } else if (isEmpty(answers)) {
      setLoading((prevState) => ({ ...prevState, answers: true }));
    } else {
      finishLoading();
    }
  }, [fetchedAnswers, answers]);

  useEffect(() => {
    setLocationError(false);
  }, [currentLocation]);

  useEffect(() => {
    if (!loading.answers && !loading.resources && typeof currentLocation !== 'string') {
      loadAssessmentResults();
    }
  }, [user?.id, currentLocation, locale, loading.answers]);

  const seeMoreResource = () => {
    setVisibleResultsCount(opportunities.length);
    setHideShowMoreButton(true);
  };

  const isRemoteEngineResult = () => {
    return results?.remote_engine_results?.length;
  };

  const getResultsTitle = () => {
    return (
      t(assessment.results_subtitle) ||
      xt(isRemoteEngineResult() && metadata?.options?.result_logic?.result_code_map?.[results.remote_engine_results[0]]?.headline) ||
      t('All finished!')
    );
  };

  const getDetailedContent = (includeWtdn) => {
    let content = [];
    if (includeWtdn) {
      content.push({ className: 'highlighted', text: 'What to do next' });
    }
    if (results?.recommendation_key) {
      content.push({ text: results.recommendation_key });
    } else if (isRemoteEngineResult() && metadata?.options?.result_logic?.result_code_map?.[results.remote_engine_results[0]]?.content?.length) {
      content = content.concat(metadata.options.result_logic.result_code_map[results.remote_engine_results[0]].content);
    }
    return content;
  };

  const openChangeLocationModal = () => {
    enqueueModal({
      name: 'ChangeAssessmentLocationModal',
      startingLocation: currentLocation,
      onLocationChange: (updatedLocation) => {
        if (currentLocation.lat !== updatedLocation.lat || currentLocation.lng !== updatedLocation.lng) {
          setCurrentLocation({ ...updatedLocation });
        }
      },
      t,
      css
    });
  };

  return (
    <Row className={`${css('assessment-step')} ${css('assessment-complete')}`}>
      <Col className={`${css(`assessment-step-result mobile-full-width-column ${cols.results.leftClass}`)}`} {...cols.results.left}>
        {loading.results ? (
          <Skeleton height={500} highlightColor={'#ffffff'} />
        ) : (
          <Container fluid className={`${css('assessment-column-container')}`}>
            <DetailedProgressSummary
              totalSteps={total}
              currentStep={total}
              title={getResultsTitle()}
              lastStepMessage={t('Results page')}
              detailedContent={getDetailedContent(metadata?.options?.result_logic?.show_wtdn)}
            />
          </Container>
        )}
      </Col>
      <Col className={`${css(`assessment-step-body mobile-full-width-column ${cols.results.rightClass}`)}`} {...cols.results.right}>
        <Container fluid>
          <Row>
            <Col className="mb-4">
              <Button
                additionalClasses={`${css('action-btn')}`}
                type="secondary"
                shape="pill"
                icon={{ style: 'fas', font: 'refresh', position: 'left' }}
                text={t(buttons.restart || 'Restart')}
                onButtonClick={() => {
                  openRestartConfirmationModal();
                }}
              />
              {/* TODO: Functionality will be built in a future ticket #9401
                <ShareResourceDropdown
                buttonStyles={shareAllButtonStyle}
                additionalClasses={`${css('action-btn')}`}
                inviteType={inviteType}
                inviteValue={inviteValue}
                shareData={shareResultData}
                shareType="allResult"
                disabled={loading.resources}
              />*/}
            </Col>
          </Row>
          <Row>
            <Col className="mb-4">
              <div className={css('assessment-location-selector-container')}>
                <FontAwesomeIcon icon={['fas', 'map-marker-alt']} className="me-2" />
                {currentLocation?.locationName}
                <Button
                  additionalClasses={`${css('link-btn')}`}
                  type="link"
                  shape="pill"
                  text={t(buttons.location || 'Change location')}
                  onButtonClick={() => openChangeLocationModal()}
                  disabled={(loading.answers || loading.resources) && !locationError}
                />
              </div>
              {locationError && <p className={css('error')}>{locationError}</p>}
            </Col>
          </Row>
          <Row>
            <Col className={css('mobile-full-width-column')}>
              {loading.resources ? (
                <Skeleton height={800} highlightColor={'#ffffff'} />
              ) : (
                <>
                  {opportunities.slice(0, visibleResultsCount).map((opp, index) => (
                    <Container key={`${index}-${opp.id}`} fluid className={css('assessment-result-container')}>
                      <ResourceResult
                        resource={opp}
                        project={'onedegree'}
                        panelButtonSettings={{
                          info: { shape: 'pill', type: 'default' },
                          map: { shape: 'pill', type: 'default' },
                          share: {
                            buttonShape: 'pill',
                            buttonType: 'default'
                          } /* needs parent to have: width: 100%; background: none !important */
                        }}
                        secondOrigin={secondOrigin}
                      />
                    </Container>
                  ))}
                  {!hideShowMoreButton && (
                    <div className={css('assessment-result-see-more-button-container')}>
                      <Button
                        type="primary"
                        shape="pill"
                        onButtonClick={() => seeMoreResource()}
                        additionalClasses={css('seeMoreResultButton')}
                        styles={{ width: 'auto' }}
                        icon={{ style: 'fas', font: 'plus', position: 'left', color: 'white' }}
                        text={t('See More Results')}
                      />
                    </div>
                  )}
                </>
              )}
            </Col>
          </Row>
          <Row>
            <Col className={css('mobile-full-width-column')}>
              <div className={css('assessment-search-resource-form-divider')}></div>
              <Container fluid className={css('assessment-column-container assessment-search-resource-form-card')}>
                <Row>
                  <Col className={css('assessment-search-resource-form-container')}>
                    <SearchResourceForm
                      buttonSettings={{
                        shape: 'pill',
                        styles: { width: 'auto' }
                      }}
                      overrideClasses={{
                        title: css('assessment-search-resource-form-title'),
                        searchField: css('assessment-search-resource-form-input'),
                        browseButton: css('assessment-search-resource-form-browse-button')
                      }}
                    />
                  </Col>
                </Row>
              </Container>
            </Col>
          </Row>
        </Container>
      </Col>
    </Row>
  );
};

export default ResultsScreen;
